<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Button, Input, Text } from '@Shelly/shelly-design-system/index';
	import { controlBleDevice } from 'src/stores/features/ble/control-device';
	import { app, os } from 'src/stores/session/native-bridge';
	import { hideBluetoothSettingsToast } from 'src/stores/ui/calibration-modals';
	import {
		control_bluetooth_device_ios,
		control_bluetooth_general_ios,
	} from 'src/services/native_container_requests_ios';
	import {
		control_bluetooth_device_android,
		control_bluetooth_general_android,
	} from 'src/services/native_container_requests_android';
	import { valueOutOfRange } from 'src/helpers/utilities';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { bindSettingsDrilldownBack, bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import {
		isBleSettingsSessionReady,
		persistBleDeviceSetting,
		readStoredBleDevice,
		resumeBleSettingsConnectionIfNeeded,
	} from 'src/helpers/ble_settings_session';
	import DynamicAlert from './DynamicAlert.svelte';

	export let device;

	const min = 5;
	const max = 300;
	let measureInterval: any = readStoredBleDevice(device)?.measureInterval || 30;
	let hadSession = false;

	$: sessionReady = isBleSettingsSessionReady($controlBleDevice.connectionState);
	$: if (sessionReady) hadSession = true;
	$: resumeBleSettingsConnectionIfNeeded(device.id, $controlBleDevice);
	$: controlsDisabled = !sessionReady || !$app;

	const goBackToSettingsList = bindSettingsDrilldownBack();

	const onSave = () => {
		hideBluetoothSettingsToast.set(false);
		if ($controlBleDevice.connectionState === 2) {
			$os === 'IOS'
				? control_bluetooth_general_ios(
						device.id,
						'setMeasureInterval',
						'measureInterval',
						measureInterval,
					)
				: control_bluetooth_general_android(
						device.id,
						'setMeasureInterval',
						'measureInterval',
						measureInterval,
					);

			persistBleDeviceSetting(device, { measureInterval });
			goBackToSettingsList?.();
		} else {
			$os === 'IOS'
				? control_bluetooth_device_ios(device.id, 'connect')
				: control_bluetooth_device_android(device.id, 'connect');
		}
	};

	$: error = valueOutOfRange(measureInterval, min, max)
		? $format('error_out_of_range', {
				default: `Value ${measureInterval} is out of range. Please provide a value within the limits. (${min} — ${max})`,
				values: {
					value: measureInterval,
					min,
					max,
					unit: $format('timer_text_sec', { default: 'sec' }),
				},
			})
		: '';

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			setStickyBottomAction({
				onClick: onSave,
				loading: false,
				disabled: controlsDisabled || !!error,
				icon: 'fas fa-save',
			});
		} else {
			setStickyBottomAction(null);
		}
	}
</script>

<DynamicAlert showExpiredMsg={hadSession} />

<Text styleType="body-small" class="mb-s">
	{$format('text_set_measurement_interval', {
		default: 'Set the measurement interval in seconds between valid measurements.',
	})}
</Text>

<Input
	aria-label={$format('input_measurement_interval', {
		default: 'Enter the measurement interval. Value must be between {min} — {max}!',
		values: { min, max },
	})}
	icon="fas fa-timer"
	class="mb-m"
	bind:value={measureInterval}
	type="number"
	step={1}
	{min}
	{max}
	disabled={controlsDisabled}
	info={$format('min_max_error_description', {
		default: 'Value must be between {min} and {max}.',
		values: { min, max },
	})}
	{error}
	id="input-yu17f63"
/>

{#if !$showNewLayout}
	<Button disabled={controlsDisabled || !!error} size="m" width="full" on:click={onSave}>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}
